// src/pages/AboutPage.js
/**
 * AboutPage
 * -----------------------------------------------------------------------------
 * Requirements covered:
 * - UKCA mark + approved body number (EN) OR "Exclusively for clinical investigations"
 * - PMDA number (JA) OR "研究目的"
 * - Manufacturer name & address
 * - App name
 * - App version injected from package.json at build time
 * - Date of manufacture injected from the build pipeline run date
 * - "Consult IFU" symbol (+ Japanese text)
 * - Intended purpose (i18n text)
 * - Indicate medical device
 * - Make software version accessible to user
 */

import React, { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LABELLING } from "../config/labelling";

import { APP_VERSION, BUILD_DATE } from "../utils/version";
import { Link } from "react-router-dom";
import { IFUIcon } from "../components/icons/IFUIcon";
import { trackEvent } from "../utils/analytics/track";

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[10ch_1fr] gap-2 justify-start items-center text-start ">
      <div className="">{label}</div>
      <div className="font-semibold">{children}</div>
    </div>
  );
}

function AboutPage() {
  const { t, i18n } = useTranslation();
  const medicalLogos = ["Asset 2.svg", "Asset 3.svg", "Asset 4.svg"];
  // Normalise to a slug we have pages for
  const lang = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "en-GB"
  ).toLowerCase();

  const hrefIFU = `https://www.electronrx.com/instructions-for-use-addenbrookes`;

  const isEN = i18n.language?.toLowerCase().startsWith("en");
  const isJA = i18n.language?.toLowerCase().startsWith("ja");

  // Regulatory display logic
  const showUKCA = isEN && LABELLING.APPROVED_BODY_NUMBER;
  const showPMDA = isJA && LABELLING.PMDA_NUMBER;

  useEffect(() => {
    trackEvent("about_view", {
      lang,
      ukca: !!showUKCA,
      pmda: !!showPMDA,
    });
  }, [lang, showUKCA, showPMDA]);

  const currentYear = new Date().getFullYear();
  const displayVersion = APP_VERSION || "-";
  const displayBuildDate = BUILD_DATE || "-";

  return (
    <div className="w-full max-w-lg h-fit min-h-full p-4 flex flex-col justify-start items-stretch gap-4">
      {/* Labelling block */}
      <section
        aria-labelledby="labelling-heading"
        className="flex flex-col gap-3"
      >
        <div className="flex flex-col gap-4">
          {/* English: UKCA + Approved Body OR Investigation-only text */}
          {isEN &&
            (showUKCA ? (
              <div className="inline-flex items-center gap-2">
                <img
                  src="/assets/ukca.svg"
                  alt="UKCA mark"
                  className="h-5 w-auto"
                  loading="lazy"
                />
                <div>
                  {t("labelling.approvedBody")}:{" "}
                  {LABELLING.APPROVED_BODY_NUMBER}
                </div>
              </div>
            ) : (
              <p>{t("labelling.investigationOnly")}</p>
            ))}

          {/* Japanese: PMDA number OR 研究目的 */}
          {/* {isJA &&
            (showPMDA ? (
              <p>{LABELLING.PMDA_NUMBER}</p>
            ) : (
              <p className="text-7xl w-full text-center">
                {t("labelling.investigationOnly")}
              </p>
            ))} */}

          {/* MD logo */}
          <div className="w-full flex items-start justify-start gap-4">
            <img
              src={`/assets/MD SVG.svg`}
              alt={`MD logo`}
              className="h-8 w-auto"
              loading="lazy"
            />
          </div>

          {/* App name */}
          <Row label={t("labelling.appName")}>{LABELLING.APP_NAME}</Row>

          {/* Version injected from package.json at build time */}
          <Row label={t("labelling.version")}>{displayVersion}</Row>

          {/* Date of manufacture / build date */}
          <Row label={t("labelling.buildDate")}>{displayBuildDate}</Row>

          {/* Manufacturer */}
          <Row
            label={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 205 255"
                className="fill-black h-6 lg:h-8 w-auto self-center"
                aria-hidden="true"
              >
                <path
                  stroke="#000"
                  strokeWidth="5"
                  d="M2.5 252.5v-120l25-50 25 50 25-50 25 50 25-50 25 50V2.5h50v250z"
                />
              </svg>
            }
          >
            <address className="flex flex-col not-italic leading-tight">
              <span>{t("company.name")}</span>
              <span>{t("company.address1")}</span>
              <span>{t("company.address2")}</span>
              <span>{t("company.address3")}</span>
            </address>
          </Row>
          <Row label={<IFUIcon className="h-8 w-8 fill-greyscale-2" />}>
            <Link
              to={hrefIFU}
              target="_blank"
              rel="noopener noreferrer"
              className=""
              onClick={() => trackEvent("about_click_ifu", { lang })}
            >
              <p className="link-text">{t("home.ifu")}</p>
            </Link>
          </Row>

          {/* Intended purpose */}
          <p> {t("labelling.intendedPurpose")}</p>
        </div>
        {/* Copyright */}
        <footer className="text-sm ">
          <p className="text-center">
            © {currentYear} {t("company.name")}.{" "}
            {t("labelling.allRightsReserved")}
          </p>
        </footer>
      </section>
    </div>
  );
}

export default memo(AboutPage);
